import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, PROFESSIONAL } from "@/data/site";
import { EASE } from "@/components/Reveal";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-paper/85 backdrop-blur-md border-b border-raiz-900/10" : "bg-transparent"
        }`}
      >
        <div
          className={`container-x flex items-center justify-between transition-all duration-500 ${
            scrolled ? "h-16" : "h-20 lg:h-24"
          }`}
        >
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3" aria-label="Sabrina Mota — Psicóloga, Início">
            <img src="/assets/tree-mark.png" alt="" className="h-8 w-auto lg:h-9" width="36" height="36" />
            <span className="leading-tight">
              <span className="block font-serif text-base lg:text-lg text-raiz-900">Sabrina Mota</span>
              <span className="block text-[10px] uppercase tracking-[0.28em] text-raiz-600">Psicóloga • Neuropsicóloga</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Navegação principal">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `relative text-[13px] tracking-wide transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-raiz-700 after:transition-all after:duration-300 ${
                    isActive ? "text-raiz-800 after:w-full" : "text-ink/65 hover:text-raiz-800 after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/agendar"
              data-testid="nav-agendar-button"
              className="rounded-full bg-raiz-800 px-5 py-2.5 text-[13px] font-medium tracking-wide text-paper transition-colors duration-300 hover:bg-raiz-700"
            >
              Agendar conversa
            </Link>
          </nav>

          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-raiz-800/25 text-raiz-800"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col bg-raiz-950 px-8 pt-28 pb-10 lg:hidden"
          >
            <nav className="flex flex-1 flex-col gap-1" aria-label="Menu móvel">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.55, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testId}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-paper/10 py-4 font-serif text-3xl font-light ${
                        isActive ? "text-raiz-200" : "text-paper"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.55, ease: EASE }}
              className="space-y-4"
            >
              <Link
                to="/agendar"
                onClick={() => setOpen(false)}
                data-testid="mobile-agendar-button"
                className="block rounded-full bg-paper py-4 text-center text-sm font-medium tracking-wide text-raiz-900"
              >
                Agendar conversa
              </Link>
              <p className="text-center text-xs text-paper/50">
                {PROFESSIONAL.fullName} — {PROFESSIONAL.crp}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
